import { AlertCircle, Archive, CheckCircle2, FilePlus2, FileSearch, History, ListChecks, MinusCircle, Repeat } from 'lucide-react';
import { Card } from '../ui/card';
import { documentYearLabel, formatEuro, statusMeta } from '../../lib/taxFlow';

const Section = ({ icon, title, count, children, isDark, testid }) => (
  <Card className={`${isDark ? '' : 'text-[#1d1d1f] '}p-5 mb-4 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'}`} data-testid={testid}>
    <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
      <span className="text-[#81d8d0]">{icon}</span>
      {title}
      {typeof count === 'number' && <span className="text-xs opacity-50">({count})</span>}
    </h3>
    {children}
  </Card>
);

const Line = ({ title, detail, right }) => (
  <li className="flex items-start gap-2 text-xs">
    <span className="text-[#81d8d0] mt-0.5">→</span>
    <div className="flex-1 min-w-0">
      <span className="font-medium">{title}</span>
      {detail && <span className="opacity-70"> — {detail}</span>}
    </div>
    {right && <span className="opacity-70 flex-shrink-0">{right}</span>}
  </li>
);

const Empty = ({ children }) => <p className="text-xs opacity-50">{children}</p>;

const fieldValue = (k, f) => {
  if (k === 'nombre_parts' || typeof f.value !== 'number') return f.value;
  return formatEuro(f.value);
};

// Bilan concret : trouvé, historique, changements, prérempli, vérifications,
// ajouts, suppressions, justificatifs manquants, cases concernées.
export const BilanView = ({ bilan, isDark }) => {
  if (!bilan) return null;
  const n = bilan.annee_revenus;
  const h = bilan.historique;
  return (
    <div data-testid="bilan-view">
      <Section icon={<FileSearch className="w-4 h-4" />} title={`Éléments trouvés dans tes documents ${n}`} count={bilan.elements_trouves.length} isDark={isDark} testid="bilan-elements">
        {bilan.elements_trouves.length === 0 ? <Empty>Aucun justificatif {n} importé pour l'instant.</Empty> : (
          <ul className="space-y-1.5">
            {bilan.elements_trouves.map((e) => (
              <Line
                key={e.id}
                title={`${e.doc_label} (${documentYearLabel(e)})`}
                detail={Object.entries(e.fields).map(([k, f]) => `${f.label || k} : ${fieldValue(k, f)}`).join(' · ') || e.filename}
              />
            ))}
          </ul>
        )}
      </Section>

      <Section icon={<History className="w-4 h-4" />} title={`Historique — revenus ${bilan.annee_revenus_n1}`} isDark={isDark} testid="bilan-historique">
        {!h ? <Empty>{bilan.profil_declarant === 'premiere' ? 'Première déclaration : pas d\'historique.' : 'Aucun avis N−1 importé.'}</Empty> : (
          <>
            <p className="text-xs opacity-70 mb-2">{h.doc_label} {h.annee_document ? `${h.annee_document}` : ''} · {h.note}</p>
            <ul className="space-y-1.5">
              {Object.entries(h.fields || {}).map(([k, f]) => <Line key={k} title={f.label || k} right={fieldValue(k, f)} />)}
              {Object.entries(h.cases || {}).map(([c, f]) => <Line key={c} title={`Case ${c}`} right={formatEuro(f.value)} />)}
            </ul>
          </>
        )}
      </Section>

      <Section icon={<Repeat className="w-4 h-4" />} title="Changements détectés" count={bilan.changements.length} isDark={isDark} testid="bilan-changements">
        {bilan.changements.length === 0 ? <Empty>Rien n'a changé par rapport à l'an dernier.</Empty> : (
          <ul className="space-y-1.5">
            {bilan.changements.map((c) => (
              <Line key={`${c.topic}-${c.status}`} title={c.title} detail={c.reason} right={<span style={{ color: statusMeta(c.status).color }}>{statusMeta(c.status).label}</span>} />
            ))}
          </ul>
        )}
      </Section>

      <Section icon={<CheckCircle2 className="w-4 h-4" />} title="Normalement prérempli : à vérifier, pas à ressaisir" count={bilan.preremplies.length} isDark={isDark} testid="bilan-preremplies">
        {bilan.preremplies.length === 0 ? <Empty>Aucune case préremplie concernée.</Empty> : (
          <ul className="space-y-1.5">
            {bilan.preremplies.map((p) => <Line key={p.topic} title={p.title} detail={`case ${p.case}`} right={p.n1_value ? `${bilan.annee_revenus_n1} : ${formatEuro(p.n1_value)}` : null} />)}
          </ul>
        )}
      </Section>

      <Section icon={<ListChecks className="w-4 h-4" />} title="Vérifications à faire" count={bilan.verifications.length} isDark={isDark} testid="bilan-verifications">
        {bilan.verifications.length === 0 ? <Empty>Rien de particulier.</Empty> : (
          <ul className="space-y-1.5">{bilan.verifications.map((v) => <Line key={v.topic} title={v.title} detail={v.detail} />)}</ul>
        )}
      </Section>

      <Section icon={<FilePlus2 className="w-4 h-4" />} title="Ajouts à faire (non préremplis)" count={bilan.ajouts.length} isDark={isDark} testid="bilan-ajouts">
        {bilan.ajouts.length === 0 ? <Empty>Aucun ajout détecté.</Empty> : (
          <ul className="space-y-1.5">
            {bilan.ajouts.map((a) => <Line key={a.topic} title={a.title} detail={`case ${a.case}`} right={a.n_value !== null && a.n_value !== undefined ? formatEuro(a.n_value) : null} />)}
          </ul>
        )}
      </Section>

      {bilan.a_supprimer.length > 0 && (
        <Section icon={<MinusCircle className="w-4 h-4" />} title={`Présent en ${bilan.annee_revenus_n1}, à ne plus déclarer`} count={bilan.a_supprimer.length} isDark={isDark} testid="bilan-supprimer">
          <ul className="space-y-1.5">{bilan.a_supprimer.map((a) => <Line key={a.topic} title={a.title} detail={a.cases.join(', ')} />)}</ul>
        </Section>
      )}

      <Section icon={<Archive className="w-4 h-4" />} title="Justificatifs manquants" count={bilan.justificatifs_manquants.length} isDark={isDark} testid="bilan-manquants">
        {bilan.justificatifs_manquants.length === 0 ? <Empty>Tous les justificatifs reconnus sont là.</Empty> : (
          <ul className="space-y-1.5">{bilan.justificatifs_manquants.map((m) => <Line key={m.topic} title={m.title} detail={m.detail} />)}</ul>
        )}
      </Section>

      {bilan.cases_concernees.length > 0 && (
        <div className="mb-4 text-xs" data-testid="bilan-cases">
          <span className="opacity-60">Cases concernées : </span>
          {bilan.cases_concernees.map((c) => (
            <code key={c} className={`mr-1.5 px-1.5 py-0.5 rounded ${isDark ? 'bg-white/10' : 'bg-black/5'}`}>{c}</code>
          ))}
        </div>
      )}

      <div className={`p-4 rounded-xl ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border mb-6`}>
        <div className="flex gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="opacity-80">{bilan.validation_notice}</p>
        </div>
      </div>
    </div>
  );
};

export default BilanView;
